import type {} from '@mui/lab/themeAugmentation';
import type {CSSProperties} from 'react';
// no need to install it explicitly
// eslint-disable-next-line import/no-unresolved
import {OverridableStringUnion} from '@mui/types';
import {Variant} from '@mui/material/styles/createTypography';
import {TypographyPropsVariantOverrides} from '@mui/material/Typography/Typography';

const fontFamily = '"Inter", sans-serif';
const letterSpacing = 'normal';

const fontStyle = {
  fontFamily,
  letterSpacing,
};

const variants = {
  h1: {
    fontSize: 48,
    fontWeight: 400,
    // 1.0833 ~ 52px to 48px
    lineHeight: 1.0833,
  },
  h2: {
    fontSize: 34,
    fontWeight: 400,
    // 1.1764 ~ 40px to 34px
    lineHeight: 1.1764,
    letterSpacing: 0.25,
  },
  h3: {
    fontSize: 24,
    fontWeight: 700,
    // 1.3333 ~ 32px to 24px
    lineHeight: 1.3333,
    letterSpacing: 0.25,
  },
  h4: {
    fontSize: 20,
    fontWeight: 700,
    // 1.4 ~ 28px to 20px
    lineHeight: 1.4,
    letterSpacing: 0.15,
  },
  h5: {
    fontSize: 14,
    fontWeight: 700,
    // 1.429 ~ 16px to 14px
    lineHeight: 1.429,
    letterSpacing: 0.15,
  },
  body1: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0.15,
  },
  body2: {
    fontSize: 14,
    fontWeight: 400,
    // 1.4286 ~ 20px to 14px
    lineHeight: 1.4286,
    letterSpacing: 0.17,
  },
  body3: {
    fontSize: 12,
    fontWeight: 400,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.17,
  },
  subtitle1: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0.15,
  },
  subtitle2: {
    fontSize: 14,
    fontWeight: 500,
    // 1.1429 ~ 16px to 14px
    lineHeight: 1.1429,
    letterSpacing: 0.1,
  },
  subtitle3: {
    fontSize: 12,
    fontWeight: 500,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.1,
  },
  captionRegular: {
    fontSize: 12,
    fontWeight: 400,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.4,
  },
  captionBold: {
    fontSize: 12,
    fontWeight: 700,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.4,
  },
  dataName: {
    fontWeight: 400,
    fontSize: 14,
    // 1.1429 ~ 16px to 14px
    lineHeight: 1.1429,
    letterSpacing: 0.17,
  },
  overlineRegular: {
    fontSize: 12,
    fontWeight: 400,
    // 1.6667 ~ 20px to 12px
    lineHeight: 1.6667,
    letterSpacing: 1,
    textTransform: 'uppercase' as CSSProperties['textTransform'],
  },
  overlineBold: {
    fontSize: 12,
    fontWeight: 700,
    // 1.6667 ~ 20px to 12px
    lineHeight: 1.6667,
    letterSpacing: 1,
    textTransform: 'uppercase' as CSSProperties['textTransform'],
  },
  cellHeader: {
    fontSize: 14,
    fontWeight: 500,
    // 1.7143 ~ 24px to 14px
    lineHeight: 1.7143,
    letterSpacing: 0.1,
  },
  cellText: {
    fontSize: 14,
    fontWeight: 400,
    // 1.7143 ~ 24px to 14px
    lineHeight: 1.7143,
    letterSpacing: 0.15,
  },
  pillTitle: {
    fontSize: 12,
    fontWeight: 400,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.15,
  },
  pillNumber: {
    fontSize: 10,
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: 0.15,
  },
  buttonLarge: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
  buttonMedium: {
    fontSize: 14,
    fontWeight: 400,
    // 1.4286 ~ 20px to 14px
    lineHeight: 1.4286,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
  buttonSmall: {
    fontSize: 12,
    fontWeight: 400,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
  tabLarge: {
    fontWeight: 600,
    fontSize: 16,
    // 1.25 ~ 20px to 16px
    lineHeight: 1.25,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
  tabMedium: {
    fontWeight: 600,
    fontSize: 14,
    // 1.1429 ~ 16px to 14px
    lineHeight: 1.1429,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
  tabSmall: {
    fontWeight: 600,
    fontSize: 12,
    // 1.3333 ~ 16px to 12px
    lineHeight: 1.3333,
    letterSpacing: 0.15,
    textTransform: 'none' as 'none',
  },
};

export type AllTypographyVariants = OverridableStringUnion<
  Exclude<Variant, 'h6' | 'button' | 'caption' | 'overline'>,
  TypographyPropsVariantOverrides
>;

const typography = {
  typography: {
    /**
     * Get correct height of the typography
     */
    height: (variant: AllTypographyVariants) =>
      Number(variants[variant].lineHeight) * Number(variants[variant].fontSize),
    ...fontStyle,
    ...variants,
  },
};

export default typography;
